import Color from 'color';
import styled, {css} from 'styled-components';

import CardSvg from '../../../../assets/icons/primary/Card.svg';
import PlusIcon from '../../../../assets/icons/secondary/Add.svg';
import StripeSvg from '../../../../assets/icons/stripe.svg';
import TaxSvg from '../../../../assets/icons/tax.svg';
import {getColor, getRem, ms} from '../../../../styles';
import {Button} from '../../../../uikit/Button';
import {IndexRow} from '../../../../uikit/IndexRow';
import {Text} from '../../../../uikit/Text';
import {TextArea} from '../../../../uikit/TextArea';
import {TextInput} from '../../../../uikit/TextInput';
import {View} from '../../../../uikit/View';
import {platform} from '../../../../utils/platform';
import {CardsListItem} from '../CardsListItem';

export const Container = styled(View)`
  flex-direction: column;
`;

export const Row = styled(IndexRow)`
  margin-bottom: ${ms(4)};
`;

export const AmountInputContainer = styled(View)`
  margin-block: ${ms(10)} ${ms(10)};
  flex-direction: column;
`;

export const AmountInput = styled(TextInput)``;

export const Description = styled(Text)`
  font-size: ${getRem(12)};
  color: ${getColor('text3')};
  letter-spacing: ${getRem(-0.24)};
  margin-bottom: ${ms(20)};
`;

export const NoteInputContainer = styled(View)`
  flex-direction: column;
  margin-block-end: ${ms(20)};
`;

export const NoteLabelContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const AddNoteButton = styled(Button).attrs({scheme: 'gray', size: 's'})`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

export const AddNoteButtonText = styled(Text)`
  font-size: ${getRem(12)};
  line-height: ${getRem(16)};
  letter-spacing: ${getRem(-0.192)};
  color: ${getColor('text3')};
`;

export const AddNoteIcon = styled(PlusIcon).attrs(p => ({
  fill: getColor('icon3')(p),
}))`
  width: ${ms(16)};
  height: ${ms(16)};
  margin-inline-end: ${ms(8)};
`;

export const NoteInput = styled(TextArea)`
  min-height: ${ms(80)};
  resize: vertical;

  ${platform.isNative &&
  css`
    height: ${ms(80)};
  `}
`;

export const TaxContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-bottom: ${ms(12)};
`;

export const Tax = styled(Text)`
  padding-left: ${ms(5)};
  font-size: ${getRem(12)};
  letter-spacing: ${getRem(-0.24)};
`;

export const TaxHighlight = styled(Text).attrs({weight: 'bold'})`
  font-size: ${getRem(12)};
`;

export const TaxIcon = styled(TaxSvg).attrs(p => ({
  fill: getColor('icon5')(p),
}))`
  width: ${ms(18)};
  height: ${ms(18)};
`;

export const Label = styled(Text).attrs({weight: 'semibold'})`
  font-size: ${getRem(16)};
  line-height: ${getRem(22)};
  letter-spacing: ${getRem(-0.46)};
  margin-block-end: ${ms(8)};
`;

export const RefundMethodsContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${ms(20)};
  position: relative;
`;

export const RefundMethodsGap = styled(View)`
  width: ${ms(10)};
`;

type RefundMethodButtonProps = {
  $isActive?: boolean;
};

export const RefundMethodButton = styled(Button)<RefundMethodButtonProps>`
  flex-direction: row;
  flex: 1;
  height: ${ms(48)};
  border-width: ${ms(1)};
  border-style: solid;
  border-color: ${getColor('background2')};
  background-color: ${getColor('background2')};
  border-radius: ${ms(10)};

  &:hover {
    background-color: ${getColor('background2')};
    border-color: ${p =>
      getColor(p.$isActive ? 'button.border2' : 'background2')(p)};
  }

  ${p =>
    p.$isActive &&
    css`
      border-color: ${getColor('button.border2')};
    `}
`;

export const RefundMethodButtonText = styled(Text).attrs({
  weight: 'bold',
})<RefundMethodButtonProps>`
  padding-left: ${ms(8)};
  color: ${getColor('text3')};
  font-size: ${getRem(13)};
  letter-spacing: ${getRem(-0.41)};

  ${p =>
    p.$isActive &&
    css`
      color: ${getColor('text1')};
    `}
`;

export const StripeIconContainer = styled(View)<RefundMethodButtonProps>`
  height: ${ms(20)};
  width: ${ms(25)};
  border-radius: ${ms(5)};
  align-items: center;
  justify-content: center;
  background-color: ${p =>
    Color(getColor(p.$isActive ? 'icon5' : 'icon3')(p))
      .lighten(0.4)
      .toString()};
`;

export const StripeIcon = styled(StripeSvg).attrs<RefundMethodButtonProps>(
  p => ({
    fill: getColor(p.$isActive ? 'icon5' : 'icon3')(p),
  }),
)<RefundMethodButtonProps>`
  width: ${ms(20)};
  height: ${ms(8)};
`;

export const CardIcon = styled(CardSvg).attrs<RefundMethodButtonProps>(p => ({
  fill: getColor(p.$isActive ? 'icon5' : 'icon3')(p),
}))<RefundMethodButtonProps>`
  width: ${ms(28)};
  height: ${ms(28)};
`;

export const StripeDescription = styled(Text)`
  font-size: ${getRem(12)};
  color: ${getColor('text3')};
  letter-spacing: ${getRem(-0.24)};
  margin-bottom: ${ms(25)};
`;

export const AddCardButton = styled(Button)`
  background-color: ${getColor('background2')};
  height: ${ms(44)};
  border-radius: ${ms(7)};
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-bottom: ${ms(20)};
  border-width: ${ms(1)};
  border-style: dashed;
  border-color: ${getColor('border5')};

  &:hover {
    background-color: ${getColor('background2')};
    border-color: ${getColor('border5')};
  }
`;

export const AddCardButtonText = styled(Text).attrs({weight: 'semibold'})`
  font-size: ${getRem(12)};
  line-height: ${getRem(16)};
  letter-spacing: ${getRem(-0.192)};
  color: ${getColor('text3')};
`;

export const AddCardIcon = styled(PlusIcon).attrs(p => ({
  fill: getColor('icon3')(p),
}))`
  width: ${ms(24)};
  height: ${ms(24)};
`;

const SCardsListItem = styled(CardsListItem)`
  margin-bottom: ${ms(10)};
`;

export {SCardsListItem as CardsListItem};

export const CardError = styled(Text)`
  position: absolute;
  left: 0;
  right: 0;
  top: ${ms(-17)};
  font-size: ${ms(11)};
  color: ${getColor('error')};
`;
